/**
 * DealForm — the "Deal" step of the sold flow.
 *
 * Ported from `salesagent-frontend-app`'s `sold-review-panel.tsx` and restyled
 * onto this app's MUI system. It's the last step of every sold scenario (sold
 * car → customer, or sold lead → vehicle): once the VIN and the customer are
 * settled, this collects the commercial side of the sale and posts it to
 * `salesagent-dashboard-backend/deal`.
 *
 * Review Max gating: the "Send Review Request" checkbox below the fields only
 * exists for dealerships with `activeFeatures.reviewMax`. The deal itself is
 * never gated — every dealership logs its sales.
 */
import React from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import Button from "../button/button";
import ControlledInput from "../input/controlled-input";
import Selector from "../select/select";
import { DEAL_TYPES } from "../../api/deal/dealApi";
import { User } from "../../types/user";
import { formatNumber } from "../../utils/utils.service";

export interface DealFormValues {
  salePrice: string;
  gross: string;
  /** Epoch milliseconds — normalised to `YYYY-MM-DD` + ts on submit. */
  saleDate: number;
  dealType: string;
  tradeIn: string;
  /** Selected staff member's uid; "" means unassigned. */
  salesPerson: string;
}

export const emptyDealValues = (): DealFormValues => ({
  salePrice: "",
  gross: "",
  saleDate: Date.now(),
  dealType: DEAL_TYPES[0],
  tradeIn: "",
  salesPerson: "",
});

/** Summary of what is being sold — rendered above the fields as a recap strip. */
export interface DealSummary {
  vehicle?: string;
  vin?: string | null;
  price?: number | string | null;
  photo?: string | null;
  customer?: string;
}

interface DealFormProps {
  values: DealFormValues;
  onChange: (patch: Partial<DealFormValues>) => void;
  users: User[];
  summary?: DealSummary;
  reviewMaxEnabled: boolean;
  isSendReview: boolean;
  setIsSendReview: (value: boolean) => void;
  loading?: boolean;
  onBack?: () => void;
  onSubmit: () => void;
}

const labelSx = { fontSize: "14px", fontWeight: "600", marginBottom: "8px" };
const inputSx = { height: "40px" };

const DealForm: React.FC<DealFormProps> = ({
  values,
  onChange,
  users,
  summary,
  reviewMaxEnabled,
  isSendReview,
  setIsSendReview,
  loading,
  onBack,
  onSubmit,
}) => {
  const staffOptions = [
    { value: "", name: "Unassigned" },
    ...users.map((user) => ({ value: user.uid, name: user.name || "Teammate" })),
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
      {summary && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            alignItems: "center",
            padding: "12px 16px",
            borderRadius: "8px",
            backgroundColor: "#F9FAFB",
            flexWrap: "wrap",
          }}
        >
          <img
            alt={summary.vehicle || "vehicle"}
            src={
              summary.photo && summary.photo !== "_NULL"
                ? summary.photo
                : "/images/car-placeholder.jpg"
            }
            width="64px"
            height="44px"
            style={{ borderRadius: "8px", objectFit: "cover" }}
          />
          <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: "600" }}>
              {summary.vehicle || "Vehicle"}
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "400",
                color: "#64748B",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {summary.vin ? `VIN: ${summary.vin}` : ""}
              {summary.customer ? `${summary.vin ? " · " : ""}${summary.customer}` : ""}
            </Typography>
          </Box>
          {Number.isFinite(Number(summary.price)) && Number(summary.price) > 0 ? (
            <Typography sx={{ fontSize: "16px", fontWeight: "700" }}>
              ${formatNumber(Number(summary.price))}
            </Typography>
          ) : null}
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography sx={labelSx}>Sale Price</Typography>
          <ControlledInput
            value={values.salePrice}
            inputProps={{ inputMode: "decimal" }}
            placeholder="0"
            sx={inputSx}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ salePrice: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={labelSx}>Gross</Typography>
          <ControlledInput
            value={values.gross}
            inputProps={{ inputMode: "decimal" }}
            placeholder="Optional"
            sx={inputSx}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ gross: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={labelSx}>Sale Date</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoItem>
              <DesktopDatePicker
                value={dayjs(values.saleDate)}
                onChange={(d) => {
                  if (d?.valueOf()) onChange({ saleDate: d.valueOf() });
                }}
                slotProps={{
                  popper: {
                    sx: { borderRadius: "8px", border: "1px solid #E2E8F0" },
                  },
                }}
                sx={{
                  backgroundColor: (theme: any) => theme.bg.paper,
                  "& .MuiOutlinedInput-input": { padding: "0", fontSize: "14px" },
                  "& > div": {
                    borderRadius: "8px !important",
                    height: "40px",
                    flexDirection: "row",
                    padding: "0 8px",
                  },
                  "& .MuiButtonBase-root .MuiPickersDay-root .Mui-selected": {
                    backgroundColor: "#297EFF",
                    color: "white",
                    borderRadius: "4px !important",
                  },
                  "& .MuiButtonBase-root": { borderRadius: "5px !important", color: "#7B7B7B" },
                  "& .MuiPickersDay-dayOutsideMonth": { color: "#d4d4d4 !important" },
                }}
              />
            </DemoItem>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={labelSx}>Deal Type</Typography>
          <Selector
            fullWidth
            data={DEAL_TYPES as unknown as string[]}
            value={values.dealType}
            onChange={(e: SelectChangeEvent) => onChange({ dealType: e.target.value as string })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={labelSx}>Salesperson</Typography>
          <Selector
            fullWidth
            withCustomMenuItems
            data={staffOptions}
            value={values.salesPerson}
            onChange={(e: SelectChangeEvent) => onChange({ salesPerson: e.target.value as string })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={labelSx}>Trade-in (optional)</Typography>
          <ControlledInput
            value={values.tradeIn}
            placeholder="e.g. 2019 Silverado 1500"
            sx={inputSx}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ tradeIn: e.target.value })
            }
          />
        </Grid>
      </Grid>

      {reviewMaxEnabled && (
        <Box sx={{ borderTop: "1px solid #E2E8F0", paddingTop: "16px" }}>
          <Box sx={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "flex-start" }}>
            <Checkbox
              checked={isSendReview}
              onChange={() => setIsSendReview(!isSendReview)}
              sx={{ padding: "0", "& .MuiSvgIcon-root": { fill: "#297EFF" } }}
            />
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                Send Review Request
              </Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#64748B" }}>
                A review request will be sent to the customer asking them to share their experience
                with this vehicle.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "8px",
          justifyContent: "flex-end",
          marginTop: "8px",
        }}
      >
        {onBack && (
          <Button
            variant="outlined"
            onClick={onBack}
            btnColor="#E2E8F0"
            sx={{ width: "fit-content", color: "#020817", borderColor: "#E2E8F0", height: "36px" }}
          >
            Back
          </Button>
        )}
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={loading}
          sx={{ width: "120px", backgroundColor: "#297EFF", height: "36px" }}
        >
          {loading ? <CircularProgress color="info" size={16} /> : "Mark as Sold"}
        </Button>
      </Box>
    </Box>
  );
};

export default DealForm;
